import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { setCurrentView } from "../../actions/viewActions";
import vpgLogo from '../../assets/images/vpg-health-logo.png';
import { motion } from 'framer-motion';
import { ChevronRight, Sparkles, Shield, TrendingUp } from 'lucide-react';

const Home = ({ setCurrentView }) => {
  useEffect(() => {
    setCurrentView("home");
  }, [setCurrentView]);

  // VPG blue color from logo - used as an accent only
  const vpgBlue = "#0056B3";

  const features = [
    {
      title: "Intuitive Interface",
      description: "Seamless experience designed for both respondents and study personnel, ensuring high completion rates and data quality",
      icon: Sparkles,
    },
    {
      title: "Advanced Analytics",
      description: "Powerful data analysis tools that transform raw data into actionable insights for better decision-making",
      icon: TrendingUp,
    },
    {
      title: "Secure & Compliant",
      description: "Enterprise-grade security meeting FDA 21 CFR Part 11 requirements and global data protection standards",
      icon: Shield,
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <img 
            src={vpgLogo} 
            alt="VPG Logo" 
            className="mx-auto h-48 w-auto"
          />
        </motion.div>

        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            The Future of
            <span className="block text-primary-600 dark:text-primary-400 mt-2">
              Flexible Outcome Assessment
            </span>
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            VPG Health's expertise in qualitative research, data analysis, and software development 
            has culminated in a groundbreaking eCOA platform. Introducing <span className="font-semibold text-primary-600 dark:text-primary-400">flexCOA™</span> — 
            where intuitive design meets cutting-edge technology.
          </p>

          <a
            href="https://vpghealth.com/ecoa/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 text-lg font-medium rounded-full text-white bg-primary-600 dark:bg-primary-500 hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors duration-300 shadow-lg hover:shadow-xl dark:shadow-gray-900/20"
          >
            Learn More
            <ChevronRight className="ml-2 w-5 h-5" />
          </a>
        </motion.div>

        {/* Features Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-24"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Built for Modern Clinical Research
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              flexCOA™ combines powerful functionality with user-friendly design to deliver 
              the most comprehensive eCOA solution available today.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 hover:shadow-lg transition-shadow duration-300 p-8 border border-gray-100 dark:border-gray-700"
              >
                <div className="flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-[#e6f3f1] dark:bg-gray-700">
                  <feature.icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-24 text-center bg-gray-50 dark:bg-gray-900 rounded-2xl p-12"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            Ready to Transform Your Clinical Trials?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
            Discover how flexCOA™ can streamline your data collection, improve patient engagement, 
            and accelerate your research outcomes.
          </p>
          <a
            href="https://vpghealth.com/ecoa/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 text-base font-medium rounded-full text-[#0056B3] bg-white dark:bg-gray-800 border-2 border-[#0056B3] dark:border-blue-400 hover:bg-[#e6f0f9] dark:hover:bg-gray-700 dark:text-blue-300 transition-colors duration-300"
          >
            Explore flexCOA™
            <ChevronRight className="ml-2 w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentView: state.views.currentView,
});

const mapDispatchToProps = {
  setCurrentView,
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);